import React, { useEffect, useState, useRef, useContext } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { orderBy } from '@progress/kendo-data-query';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { UserContext } from 'contexts/user-context';
import { useBoolean, useTranslation } from 'hooks';
import {
    SiteFields,
    OrganisationMemberFields,
    StudyFields,
    StudyMemberFields,
} from 'queries/generated/fragments.generated';
import { GET_ALL_SITES } from 'queries/sites';
import {
    GET_STUDY,
    GET_USER_STUDIES,
    SET_SM_STUDY_FAVOURITE,
    SET_SM_STUDY_SITE_DEFAULT,
    SET_SM_STUDY_SITE_FAVOURITE,
    SET_STUDY_DEFAULT,
} from 'queries/studies';
import ExpandableGrid from 'views/components/core/expandable/grid';
import Filter from 'views/components/core/filter';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';
import ItemWrapper from 'views/components/core/item-wrapper';
import Loading from 'views/components/core/loading';
import RedesignedRadioButton from 'views/components/core/radio-button';
import 'views/pages/uam/study-membership/styles.scss';
import Error from 'views/components/error/error';
import { notEmpty } from '@utils/utility-helpers';

const SmStudyMembership = ({
    user,
    organisationMember,
    errorNotification,
}: {
    user: any;
    organisationMember: OrganisationMemberFields;
    errorNotification: Function;
}) => {
    const { t } = useTranslation();
    const userData = useContext(UserContext);
    const [filteredStudies, setFilteredStudies] = useState<StudyFields[]>();
    const [filteredSites, setFilteredSites] = useState<SiteFields[]>();
    const [allStudies, setAllStudies] = useState<[]>();
    const [allSites, setAllSites] = useState<[]>();
    const [studiesFilter, setStudiesFilter] = useState('');
    const [sitesFilter, setSitesFilter] = useState('');
    const [amountStudies, setAmountStudies] = useState(0);
    const [amountSites, setAmountSites] = useState(0);
    const initialShowSites = useBoolean(false);
    const isFirstTimeStudy = useBoolean(true);
    const isFirstTimeSite = useBoolean(true);
    const [favouriteSiteIds, setFavouriteSiteIds] = useState([]);
    const [studyMember, setStudyMember] = useState({ favouriteSiteIds: [] } as any);
    const [selectedStudy, setSelectedStudy] = useState({
        id: '' as string,
        studyNumber: '' as string,
        members: [] as any,
    });
    const { data, error, loading } = useQuery(GET_USER_STUDIES, {
        variables: { userId: user.id, organisationKey: user.organisationKey },
    });
    const [defaultStudyId, setDefaultStudyId] = useState(organisationMember.defaultStudyId);
    const [defaultSiteId, setDefaultSiteId] = useState('');
    const [setStudyFavourite] = useMutation(SET_SM_STUDY_FAVOURITE);
    const [setStudyDefault] = useMutation(SET_STUDY_DEFAULT);
    const [setStudySiteFavourite] = useMutation(SET_SM_STUDY_SITE_FAVOURITE);
    const [setStudySiteDefault] = useMutation(SET_SM_STUDY_SITE_DEFAULT);
    const [loadSitesData, sitesResponse] = useLazyQuery(GET_ALL_SITES);
    const [loadStudyData, studyResponse] = useLazyQuery(GET_STUDY);
    const scrollToRef = (ref: any) =>
        window.scrollTo({
            top: ref.current?.offsetTop,
            behavior: 'smooth',
        });
    const sitesRef = useRef(null);
    const executeScroll = () => scrollToRef(sitesRef);

    const sortStudies = (studies: StudyFields[]) =>
        orderBy(studies, [
            { field: 'isDefault', dir: 'desc' },
            { field: 'isFavourite', dir: 'desc' },
            { field: 'studyNumber', dir: 'asc' },
        ]);

    const sortSites = (studies: StudyFields[]) =>
        orderBy(studies, [
            { field: 'isDefault', dir: 'desc' },
            { field: 'isFavourite', dir: 'desc' },
            { field: 'siteName', dir: 'asc' },
        ]);

    useEffect(() => {
        if (data?.userStudies && isFirstTimeStudy.value) {
            const array = data.userStudies
                .map((item: StudyFields) => {
                    const member = item?.smMembers?.find((s) => s?.userId === user.id);
                    return {
                        ...item,
                        isDefault: item.id === defaultStudyId,
                        isFavourite: member?.favourite ?? false,
                        isAdmin: member?.isAdmin ?? false,
                        active: member?.active,
                    };
                })
                .filter((item: any) => item.active);
            const sortedSites = sortStudies(array);
            setFilteredStudies(sortedSites);
            setAllStudies(array);
        }
    }, [data]);

    useEffect(() => {
        if (sitesResponse.data?.allSites && studyResponse?.data?.study && isFirstTimeSite.value) {
            const { sitesStatusesToExclude } = studyResponse.data.study;
            const array = sitesResponse.data.allSites
                .map((item: SiteFields) => {
                    const isFavoriteSite = studyMember?.favouriteSiteIds.some((fav: string) => fav === item.id);
                    const siteToShow = sitesStatusesToExclude.indexOf(item.status) < 0;
                    return {
                        ...item,
                        isDefault: item.id === defaultSiteId,
                        isFavourite: isFavoriteSite,
                        siteToShow,
                    };
                })
                .filter((item: any) => item.siteToShow);
            const sortedSites = sortSites(array);
            setFilteredSites(sortedSites);
            setAllSites(array);
        }
    }, [sitesResponse, studyMember, studyResponse]);

    useEffect(() => {
        if (selectedStudy && !sitesResponse.loading) executeScroll();
    }, [selectedStudy, sitesResponse.loading]);

    useEffect(() => {
        if (studyResponse.data && (selectedStudy?.id === studyResponse.data.study?.id || !selectedStudy)) {
            studyResponse.data.study?.smMembers.forEach((member: StudyMemberFields) => {
                if (member.userId === user.id) setStudyMember(member);
            });
        }
    }, [studyResponse]);

    const [favsStudies, setFavsStudies] = useState(0);

    const getAvailableStudies = () => {
        const studiesData = filteredStudies ? [...filteredStudies] : [];
        return studiesData?.filter((item: StudyFields) =>
            !studiesFilter.length ? true : (item.studyNumber?.toUpperCase()?.indexOf(studiesFilter) ?? 0) >= 0,
        );
    };

    const getAvailableSites = () => {
        const sitesData = filteredSites ? [...filteredSites] : [];
        return sitesData?.filter((item) =>
            !sitesFilter.length ? true : item.name.toUpperCase().indexOf(sitesFilter) >= 0,
        );
    };

    const handleFavouriteSiteIds = (member: any, id?: string) => {
        const favouriteSiteIdsArray = [...member.favouriteSiteIds].filter(
            (item: string) => getAvailableSites().findIndex((el: SiteFields) => el.id === item) >= 0,
        );
        if (id) {
            const index = favouriteSiteIdsArray.indexOf(id);
            if (index >= 0) favouriteSiteIdsArray.splice(index, 1);
            else favouriteSiteIdsArray.push(id);
        }
        setFavouriteSiteIds(favouriteSiteIdsArray);
    };

    useEffect(() => {
        handleFavouriteSiteIds(studyMember);
    }, [studyMember, filteredSites]);

    const findDefaultSite = (members: StudyMemberFields[]) => {
        members.forEach((member: StudyMemberFields) => {
            if (member.userId === user.id) {
                setDefaultSiteId(member.defaultSiteId);
                setStudyMember(member);
            }
        });
    };

    const favoriteAmount = (all: any) => {
        let count: number = 0;
        if (all) {
            all.forEach((s: any) => {
                s.smMembers.forEach((member: StudyMemberFields) => {
                    if (member.userId === user.id && member.favourite) count++;
                });
            });

            setFavsStudies(count);
        }
    };

    useEffect(() => {
        favoriteAmount(data?.userStudies);
    }, [data?.userStudies]);

    const setFavoriteStudy = (studyId: string, isFavourite: boolean, isSetDefault?: boolean) => {
        isFirstTimeStudy.setFalse();
        setStudyFavourite({
            variables: {
                studyId,
                favourite: isFavourite,
            },
            refetchQueries: [
                {
                    query: GET_USER_STUDIES,
                    variables: {
                        userId: user.id,
                        organisationKey: user.organisationKey,
                    },
                },
            ],
        })
            .then(() => {
                if (isSetDefault) {
                    NavigationService.setUpdateStudies(userData?.user?.organisationKey ?? '', undefined, studyId, true);
                } else {
                    NavigationService.setUpdateStudies(userData?.user?.organisationKey ?? '');
                }
                updateFilteredStudies(studyId, isSetDefault, isFavourite);
            })
            .catch(() => {
                errorNotification();
            });
    };

    const setFavoriteSite = (studyId: string, siteId: string, isFavourite: boolean, isSetDefault?: boolean) => {
        isFirstTimeSite.setFalse();
        setStudySiteFavourite({
            variables: {
                studyId,
                siteId,
                favourite: isFavourite,
            },
            refetchQueries: [
                {
                    query: GET_STUDY,
                    variables: {
                        id: studyId,
                    },
                },
            ],
        })
            .then(() => {
                handleFavouriteSiteIds(studyMember, siteId);
                if (isSetDefault) updateFilteredSites(siteId, true, isFavourite);
                else updateFilteredSites(siteId, undefined, isFavourite);
                NavigationService.setUpdateStudies(userData?.user?.organisationKey ?? '');
            })
            .catch(() => {
                errorNotification();
            });
    };

    const updateFilteredStudies = (studyId: string, isDefault?: boolean, isFavourite?: boolean) => {
        const array = filteredStudies?.filter(notEmpty).map((item: StudyFields) => {
            if (item.id === studyId) {
                if (isFavourite !== undefined && isDefault !== undefined)
                    return {
                        ...item,
                        isFavourite,
                        isDefault,
                    };
                if (isDefault !== undefined)
                    return {
                        ...item,
                        isDefault,
                    };
                if (isFavourite !== undefined)
                    return {
                        ...item,
                        isFavourite,
                    };
            } else {
                if (isDefault !== undefined)
                    return {
                        ...item,
                        isDefault: false,
                    };
                return item;
            }
            return item;
        });
        setFilteredStudies(array);
    };

    const updateFilteredSites = (siteId: string, isDefault?: boolean, isFavourite?: boolean) => {
        const array = filteredSites?.filter(notEmpty).map((item: SiteFields) => {
            if (item.id === siteId) {
                if (isFavourite !== undefined && isDefault !== undefined)
                    return {
                        ...item,
                        isFavourite,
                        isDefault,
                    };
                if (isDefault !== undefined)
                    return {
                        ...item,
                        isDefault,
                    };
                if (isFavourite !== undefined)
                    return {
                        ...item,
                        isFavourite,
                    };
            } else {
                if (isDefault !== undefined)
                    return {
                        ...item,
                        isDefault: false,
                    };
                return item;
            }
            return item;
        });
        setFilteredSites(array);
    };

    const setStudyAsDefault = (studyId: string, isFavourite?: boolean) => {
        isFirstTimeStudy.setFalse();
        setStudyDefault({
            variables: {
                studyId,
            },
        })
            .then(() => {
                setDefaultStudyId(studyId);
                userData.setStudyId(studyId);
                if (!isFavourite) setFavoriteStudy(studyId, true, true);
                else {
                    updateFilteredStudies(studyId, true, isFavourite);
                    NavigationService.setUpdateStudies(userData?.user?.organisationKey ?? '', undefined, studyId, true);
                }
            })
            .catch(() => {
                errorNotification();
            });
    };

    const setSiteAsDefault = (studyId: string, siteId: string, isFavourite?: boolean) => {
        isFirstTimeSite.setFalse();
        setStudySiteDefault({
            variables: {
                studyId,
                siteId,
            },
        })
            .then(() => {
                setDefaultSiteId(siteId);
                userData.setSiteId(siteId);
                if (isFavourite) {
                    updateFilteredSites(siteId, true);
                    NavigationService.setUpdateStudies(userData?.user?.organisationKey ?? '');
                } else setFavoriteSite(studyId, siteId, true, true);
            })
            .catch(() => {
                errorNotification();
            });
    };

    if (loading) return <Loading />;

    if (error) return <Error error={error} />;

    return (
        <div className="study-membership-wrap">
            <ItemWrapper title={t('uam:smStudyMembership')} customRef={null} hideItemNumber>
                <div className="members-ship">
                    <div className="filter-item-mb">
                        <Filter
                            id="study-membership-filter"
                            onChange={({ text }: { text: string }) => setStudiesFilter(text)}
                        />
                    </div>
                    <div className="list-info">
                        <p className="text-length">
                            {amountStudies} {t('uam:studyRecords')}
                        </p>
                    </div>
                    <ExpandableGrid
                        maxHeight={396}
                        customKey="membership-grid"
                        id="membership-grid"
                        key="membershipGrid"
                        items={getAvailableStudies()}
                        groupable={false}
                        reorderable
                        scrollable="scrollable"
                        showPager={false}
                        resizable
                        onUpdateAmount={setAmountStudies}
                        addScroll={false}
                        pageable={false}
                        showFirstColumn={false}
                        columns={[
                            {
                                label: t('uam:default'),
                                field: 'isDefault',
                                id: 'uam:default',
                                filterUi: GridColumnMenuActiveFilter,
                                minWidth: 150,
                                cells: {
                                    data: ({ dataItem }: any) => (
                                        <td className="cursor-point">
                                            <RedesignedRadioButton
                                                checked={dataItem.isDefault}
                                                onChange={() => {
                                                    setStudyAsDefault(dataItem.id, dataItem.isFavourite);
                                                }}
                                            />
                                        </td>
                                    ),
                                },
                            },
                            {
                                label: t('uam:favorite'),
                                id: 'uam:favorite',
                                field: 'isFavourite',
                                minWidth: 140,
                                filterUi: GridColumnMenuActiveFilter,
                                cells: {
                                    data: ({ dataItem }: any) => {
                                        const star = dataItem.isFavourite ? (
                                            <FontAwesomeIcon icon={['fas', 'star']} className="fa-lg star" />
                                        ) : (
                                            <FontAwesomeIcon icon={['far', 'star']} className="fa-lg star" />
                                        );

                                        return (
                                            <td>
                                                <span
                                                    className="cursor-point"
                                                    onClick={() => {
                                                        if (selectedStudy?.id === dataItem.id)
                                                            loadStudyData({ variables: { id: dataItem.id } }).catch(
                                                                (err) => window.console.error(err),
                                                            );
                                                        setFavoriteStudy(dataItem.id, !dataItem.isFavourite);
                                                    }}
                                                >
                                                    {star}
                                                </span>
                                            </td>
                                        );
                                    },
                                },
                            },
                            {
                                label: t('uam:study'),
                                field: 'studyNumber',
                                id: 'uam:studyName',
                                minWidth: 150,
                                cells: {
                                    data: ({ dataItem }: any) => (
                                        <td
                                            onClick={() => {
                                                if (dataItem.numSites > 0) {
                                                    isFirstTimeSite.setTrue();
                                                    initialShowSites.setTrue();
                                                    loadSitesData({ variables: { studyId: dataItem.id } }).catch(
                                                        (err) => window.console.error(err),
                                                    );
                                                    loadStudyData({ variables: { id: dataItem.id } }).catch((err) =>
                                                        window.console.error(err),
                                                    );
                                                    setSelectedStudy(dataItem);
                                                    findDefaultSite(dataItem.members);
                                                }
                                            }}
                                        >
                                            <span
                                                className={`cursor-point  ${
                                                    dataItem.numSites > 0 ? 'study-underline' : ''
                                                }`}
                                            >
                                                {dataItem.studyNumber}
                                            </span>
                                        </td>
                                    ),
                                },
                            },
                            {
                                label: t('uam:studyTeamAdministrator'),
                                id: 'uam:administrator',
                                filterUi: GridColumnMenuActiveFilter,
                                field: 'isAdmin',
                                minWidth: 250,
                                cells: {
                                    data: ({ dataItem }: any) => {
                                        const adminIcon = dataItem.isAdmin ? (
                                            <FontAwesomeIcon icon={['fas', 'user-cog']} className="fa-lg admin-icon" />
                                        ) : null;

                                        return (
                                            <td>
                                                <span>{adminIcon}</span>
                                            </td>
                                        );
                                    },
                                },
                            },
                        ]}
                    />
                    <div className="selected-items-container">
                        <p>
                            {' '}
                            {favsStudies} {t('uam:favorites')}
                        </p>
                    </div>
                </div>
            </ItemWrapper>

            <div className="sites-wrap" ref={sitesRef}>
                {sitesResponse.loading ? (
                    <Loading />
                ) : initialShowSites.value ? (
                    <ItemWrapper
                        title={`${t('uam:siteForStudy')} ${selectedStudy.studyNumber}`}
                        customRef={null}
                        hideItemNumber
                    >
                        <div className="members-ship">
                            <div className="filter-item-mb">
                                <Filter
                                    id="sites-membership-filter"
                                    onChange={({ text }: { text: string }) => setSitesFilter(text)}
                                />
                            </div>
                            <div className="list-info">
                                <p className="text-length">
                                    {amountSites} {t('uam:siteRecords')}
                                </p>
                            </div>
                            <ExpandableGrid
                                maxHeight={396}
                                customKey="sites-grid"
                                id="sites-grid"
                                key="sitesGrid"
                                items={getAvailableSites()}
                                groupable={false}
                                reorderable
                                scrollable="scrollable"
                                showPager={false}
                                resizable
                                addScroll={false}
                                pageable={false}
                                showFirstColumn={false}
                                onUpdateAmount={setAmountSites}
                                columns={[
                                    {
                                        label: t('uam:default'),
                                        field: 'isDefault',
                                        filterUi: GridColumnMenuActiveFilter,
                                        id: 'site:default',
                                        minWidth: 120,
                                        cells: {
                                            data: (site: any) => (
                                                <td className="cursor-point">
                                                    <RedesignedRadioButton
                                                        checked={site.dataItem.isDefault}
                                                        onChange={() => {
                                                            setSiteAsDefault(
                                                                selectedStudy.id,
                                                                site.dataItem.id,
                                                                site.dataItem.isFavourite,
                                                            );
                                                        }}
                                                    />
                                                </td>
                                            ),
                                        },
                                    },
                                    {
                                        label: t('uam:favorite'),
                                        field: 'isFavourite',
                                        filterUi: GridColumnMenuActiveFilter,
                                        id: 'site:favorite',
                                        minWidth: 100,
                                        cells: {
                                            data: (site: any) => {
                                                const star = site.dataItem.isFavourite ? (
                                                    <FontAwesomeIcon icon={['fas', 'star']} className="fa-lg star" />
                                                ) : (
                                                    <FontAwesomeIcon icon={['far', 'star']} className="fa-lg star" />
                                                );

                                                return (
                                                    <td>
                                                        <span
                                                            className="cursor-point"
                                                            onClick={() =>
                                                                setFavoriteSite(
                                                                    selectedStudy.id,
                                                                    site.dataItem.id,
                                                                    !site.dataItem.isFavourite,
                                                                )
                                                            }
                                                        >
                                                            {star}
                                                        </span>
                                                    </td>
                                                );
                                            },
                                        },
                                    },
                                    {
                                        label: t('uam:site'),
                                        field: 'name',
                                        id: 'site:siteName',
                                        minWidth: 150,
                                    },
                                ]}
                            />
                            <div className="selected-items-container">
                                <p>
                                    {' '}
                                    {favouriteSiteIds.length} {t('uam:favorites')}
                                </p>
                            </div>
                        </div>
                    </ItemWrapper>
                ) : null}
            </div>
        </div>
    );
};

export default SmStudyMembership;
